import { createContractLink } from '@modusign/openapi-client-template';
import {
  CreateContractLinkRequestDto,
  CreateContractLinkResponseDto,
} from '@modusign/openapi-client-template/model';
import { useDocument, useSigners, useVerification } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/setup';
import { toDocumentModelForSetup } from 'features/apply-react-signing/libs/signing-mapper';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';

import { getContractLinkCreateState } from '@app/features/contractLink/contractLinkCreate';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import workspaceService from '@app/libs/workspaceService';

const CREATE_CONTRACT_LINK = 'CREATE_CONTRACT_LINK';

export function useCreateContractLink() {
  const { labels, maxSigningPerParticipant, upload } = useSelector(getContractLinkCreateState);

  const { signers } = useSigners();
  const { document } = useDocument();
  const { signingState } = useSigningState();
  const data = toDocumentModelForSetup(signingState);
  // (@Juyeong-Byeon): 링크서명자는 1명이기 때문에 signers 중 첫번째 유저를 가져와서 사용한다.
  const contractLinkSigner = signers[0];

  const { verification } = useVerification(contractLinkSigner.id);

  return useMutation<CreateContractLinkResponseDto, Error, void>(
    CREATE_CONTRACT_LINK,
    async () => {
      if (!upload.fileId || !document.title) {
        /**
         * TODO: @Juyeong-Byeon
         * 에러처리 방향 결정 후 수정
         * Depend on :#9103
         */
        throw new Error(
          `Required fileId or document title, but got ${upload.fileId} ${document.title}`
        );
      }

      const createContractLinkResponse = await createContractLink(
        workspaceService.getWorkspaceId(),
        {
          title: document.title,
          file: {
            id: upload.fileId,
          },
          fieldGroups: data.fieldGroups,
          fields: data.fields as CreateContractLinkRequestDto['fields'],
          participants: data.participants as CreateContractLinkRequestDto['participants'],
          attachments: data.attachments as CreateContractLinkRequestDto['attachments'],
          linkPassword: null,
          /**
           * 중복 서명을 허용하지 않는 경우 1로 전달합니다.
           * state 타입을 number로 변경하지 않은 이유는 허용, 허용 안함 상태를 변경할 때 input 값을
           * 변경하는 것보다 최종적으로 서버에 전송하기 전에 기본 값을 설정하는게 간단하다고 판단했습니다.
           */
          maxSigningPerSigner: maxSigningPerParticipant ?? 1,
          mobileIdentificationRequired:
            verification?.methods.some((method) => method.type === 'MOBILE_IDENTIFICATION') ??
            false,
          messageFromRequester: contractLinkSigner.message?.trim() || null,
          labelIds: labels.map((l) => l.id),
          /**
           * 현재 링크서명 생성 시 roleGrants를 사용하지 않습니다.
           */
          roleGrants: [],
        }
      );

      return createContractLinkResponse;
    },
    {
      onSuccess: ({ status }) => {
        pushDataLayer({ event: 'createLinkSigning', linkSigningStatus: status });
      },
    }
  );
}
